import { HttpError, type FilterPayload } from 'ra-core';
import { ChipProps } from '@mui/material';
import {
  JsonApiHasManyRelationship,
  JsonApiHasOneRelationship,
  JsonApiResource,
} from './DataProvider';
import { isNil } from 'lodash';

export const DEFAULT_LIST_PAGE_IMAGE_MAX_WIDTH = 50;
export const DEFAULT_LIST_PAGE_IMAGE_MAX_HEIGHT = 50;
export const DEFAULT_LIST_PAGE_PAGINATION_LIMIT = 25;

type TypeGuard<A, B extends A> = (value: A) => value is B;
type NegatedTypeGuard<A, B extends A> = <T extends A>(value: T) => value is Exclude<T, B>;

export function not<A, B extends A>(predicate: TypeGuard<A, B>): NegatedTypeGuard<A, B>;
export function not<A>(predicate: (value: A) => boolean): (value: A) => boolean;
export function not(predicate: (value: unknown) => boolean): (value: unknown) => boolean {
  return (value) => !predicate(value);
}

export function isNonEmptyString(value: unknown): value is Exclude<string, ''> {
  return typeof value === 'string' && value.trim() !== '';
}

export function isNumericString(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(value) && !Number.isNaN(parseFloat(value));
}

type HasOneKey<T extends JsonApiResource> = {
  [K in keyof T['relationships']]: T['relationships'][K] extends JsonApiHasOneRelationship<string>
    ? K
    : never;
}[keyof T['relationships']];

type HasManyKey<T extends JsonApiResource> = {
  [K in keyof T['relationships']]: T['relationships'][K] extends JsonApiHasManyRelationship<string>
    ? K
    : never;
}[keyof T['relationships']];

export function idFrom<T extends JsonApiResource>(
  data: T | null | undefined,
  key: HasOneKey<T>,
): undefined | string {
  if (isNil(data) || key in data.relationships === false) {
    return undefined;
  }

  const relationship = data.relationships[key] as JsonApiHasOneRelationship<string>;

  return relationship?.data?.id;
}

export function idsFrom<T extends JsonApiResource>(
  data: T | null | undefined,
  key: HasManyKey<T>,
): undefined | string[] {
  if (isNil(data) || key in data.relationships === false) {
    return undefined;
  }

  const relationship = data.relationships[key] as JsonApiHasManyRelationship<string>;

  return relationship?.data?.map((item) => item.id);
}

export interface TruncationOptions {
  omission?: string;
  locale?: string;
  granularity?: 'grapheme' | 'word' | 'sentence';
}
export function truncateString(
  value: string,
  length: number,
  options: TruncationOptions = {},
): string {
  if (value.length <= length) {
    return value;
  }

  const { omission = '...', locale, granularity = 'word' } = options;

  const segmenter = new Intl.Segmenter(locale, { granularity });
  const segments = [...segmenter.segment(value)];

  return segments.reduce((acc: string, segment: Intl.SegmentData) => {
    const currentLength = acc.length;
    const segmentLength = segment.segment.length;
    const omissionLength = omission.length;

    // If adding the segment would not exceed the length, add it
    if (
      currentLength + segmentLength <= length - omissionLength &&
      !acc.trimEnd().endsWith(omission)
    ) {
      return acc + segment.segment;
    }

    // If we've not already added the omission, add it
    if (!acc.trimEnd().endsWith(omission)) {
      return acc + omission;
    }

    // We can't add the segment and have already added the omission
    return acc.trimEnd();
  }, '');
}

export const omit = <T extends object, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K> => {
  const result = { ...obj };
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  keys.forEach((key) => delete result[key]);
  return result;
};

export function removeUndefinedKeys<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

export function createFilterQuery(
  fields: string[] = ['name'],
  filterType = 'contains',
): (query: string) => FilterPayload {
  return (query: string) => {
    const filter = {
      [filterType]: fields.reduce((acc, field) => ({ ...acc, [field]: query }), {}),
    };

    return query.match(/^\d+$/) ? { or: { id: query, ...filter } } : filter;
  };
}

export function entry(value: string): { id: string; name: string } {
  return { id: value, name: value };
}

export function id<T>(value: T): T {
  return value;
}

export function ignore(): void {
  return;
}

export type DeepPartial<T> = T extends unknown[]
  ? T
  : T extends Record<string, unknown>
    ? {
        [P in keyof T]?: DeepPartial<T[P]>;
      }
    : T;

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
export interface Flag {
  id: number;
  name: string;
  color?: ChipProps['color'];
}

export function getJsonApiErrorDetail(error: unknown, fallback = 'Request failed'): string {
  if (error instanceof HttpError && error.body && typeof error.body === 'object') {
    const body = error.body as { errors?: { detail?: string }[]; message?: unknown };
    const detail = body.errors?.[0]?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }

    if (typeof body.message === 'string' && body.message.length > 0) {
      return body.message;
    }
  }

  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  return fallback;
}
